import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, query } from 'firebase/firestore';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import { styled } from '@mui/system';
import CastForEducationIcon from '@mui/icons-material/CastForEducation';

// Modern color palette (consistent with other components)
const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  background: '#f5f7fa',
  accent: '#3498db',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
};

// Styled components
const ModernCard = styled(Box)(({ theme }) => ({
  borderRadius: '16px',
  background: 'white',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
  padding: theme.spacing(3),
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1.25, 2.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: colors.accent,
    color: 'white',
  },
}));

const AddTopics = () => {
  const [topic, setTopic] = useState({
    name: '',
    description: '',
    backgroundColor: '#ffffff',
    subjectId: '',
    schoolLevelId: '',
  });
  const [subjects, setSubjects] = useState([]);
  const [schoolLevels, setSchoolLevels] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingSchoolLevels, setLoadingSchoolLevels] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const colorOptions = [
    { value: '#ffffff', label: 'White' },
    { value: '#f8d7da', label: 'Light Red' },
    { value: '#d4edda', label: 'Light Green' },
    { value: '#cce5ff', label: 'Light Blue' },
    { value: '#fff3cd', label: 'Light Yellow' },
    { value: '#e2e3e5', label: 'Light Gray' },
    { value: '#f5c6cb', label: 'Soft Pink' },
    { value: '#c3e6cb', label: 'Mint Green' },
    { value: '#b8daff', label: 'Sky Blue' },
    { value: '#ffeeba', label: 'Pale Yellow' },
    { value: '#d6bcfa', label: 'Lavender' },
    { value: '#fdd0e0', label: 'Baby Pink' },
    { value: '#b5f5e2', label: 'Aqua' },
    { value: '#fed7aa', label: 'Peach' },
    { value: '#e9d8fd', label: 'Lilac' },
    { value: '#a3bffa', label: 'Periwinkle' },
    { value: '#fed7d7', label: 'Coral' },
    { value: '#bef264', label: 'Lime' },
    { value: '#bbf7d0', label: 'Pistachio' },
    { value: '#bfdbfe', label: 'Cornflower Blue' },
  ];

  useEffect(() => {
    const fetchSubjects = async () => {
      setLoadingSubjects(true);
      try {
        const subjectsCollection = collection(db, 'subjects');
        const subjectsQuery = query(subjectsCollection);
        const querySnapshot = await getDocs(subjectsQuery);
        const subjectsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSubjects(subjectsData);
      } catch (error) {
        console.error('Error fetching subjects:', error);
      } finally {
        setLoadingSubjects(false);
      }
    };

    fetchSubjects();
  }, []);

  useEffect(() => {
    const fetchSchoolLevels = async () => {
      setLoadingSchoolLevels(true);
      try {
        const schoolLevelsCollection = collection(db, 'schoolLevels');
        const schoolLevelsQuery = query(schoolLevelsCollection);
        const querySnapshot = await getDocs(schoolLevelsQuery);
        const schoolLevelsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSchoolLevels(schoolLevelsData);
      } catch (error) {
        console.error('Error fetching school levels:', error);
      } finally {
        setLoadingSchoolLevels(false);
      }
    };

    fetchSchoolLevels();
  }, []);

  const handleChange = (e) => {
    setTopic({ ...topic, [e.target.name]: e.target.value });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'topics'), {
        name: topic.name,
        description: topic.description,
        backgroundColor: topic.backgroundColor,
        subjectId: topic.subjectId,
        schoolLevelId: topic.schoolLevelId,
        createdAt: new Date(),
      });
      setTopic({ name: '', description: '', backgroundColor: '#ffffff', subjectId: '', schoolLevelId: '' });
      setSnackbarMessage('Topic added successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error adding topic: ', error);
      setSnackbarMessage('Error adding topic');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  return (
    <div style={{ background: colors.background }}>
      <Container sx={{ py: 8, minHeight: '100vh', mt: 5 }}>
        {/* Header */}
        <Box
          sx={{
            mb: 5,
            p: 4,
            borderRadius: '16px',
            background: `linear-gradient(159deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
            color: 'white',
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: '700', display: 'flex', alignItems: 'center' }}>
            <CastForEducationIcon sx={{ fontSize: 35, mr: 1 }} />
            Add New Topic
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
            Create a new topic for the portal.
          </Typography>
        </Box>

        {/* Form */}
        <ModernCard sx={{ maxWidth: 600, mx: 'auto' }}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Topic Name"
              name="name"
              value={topic.name}
              onChange={handleChange}
              required
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: colors.primary },
                  '&:hover fieldset': { borderColor: colors.accent },
                  '&.Mui-focused fieldset': { borderColor: colors.accent },
                },
                '& .MuiInputLabel-root': {
                  color: colors.secondary,
                  '&.Mui-focused': { color: colors.accent },
                },
              }}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={topic.description}
              onChange={handleChange}
              multiline
              rows={4}
              required
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: colors.primary },
                  '&:hover fieldset': { borderColor: colors.accent },
                  '&.Mui-focused fieldset': { borderColor: colors.accent },
                },
                '& .MuiInputLabel-root': {
                  color: colors.secondary,
                  '&.Mui-focused': { color: colors.accent },
                },
              }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: colors.secondary }}>Background Color</InputLabel>
              <Select
                name="backgroundColor"
                value={topic.backgroundColor}
                onChange={handleChange}
                sx={{
                  borderRadius: '10px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                }}
              >
                {colorOptions.map((color) => (
                  <MenuItem key={color.value} value={color.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          backgroundColor: color.value,
                          mr: 1,
                          border: '1px solid #ccc',
                        }}
                      />
                      {color.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: colors.secondary }}>Subject</InputLabel>
              <Select
                name="subjectId"
                value={topic.subjectId}
                onChange={handleChange}
                disabled={loadingSubjects || subjects.length === 0}
                sx={{
                  borderRadius: '10px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                }}
              >
                <MenuItem value="">
                  <em>{loadingSubjects ? 'Loading subjects...' : 'None'}</em>
                </MenuItem>
                {subjects.map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ color: colors.secondary }}>School Level</InputLabel>
              <Select
                name="schoolLevelId"
                value={topic.schoolLevelId}
                onChange={handleChange}
                disabled={loadingSchoolLevels || schoolLevels.length === 0}
                sx={{
                  borderRadius: '10px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                }}
              >
                <MenuItem value="">
                  <em>{loadingSchoolLevels ? 'Loading school levels...' : 'None'}</em>
                </MenuItem>
                {schoolLevels.map((level) => (
                  <MenuItem key={level.id} value={level.id}>
                    {level.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <AnimatedButton
              type="submit"
              variant="contained"
              color="primary"
            >
              Add Topic
            </AnimatedButton>
          </form>
        </ModernCard>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: '100%', bgcolor: snackbarSeverity === 'success' ? colors.primary : '#e74c3c', color: 'white' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
};

export default AddTopics;